import { useEffect, useState, useContext, useRef, useCallback } from 'react';
import { useForm, zodResolver } from '@mantine/form';
import { useMutation } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';

import { UserContext } from '~/components/providers/UserProvider';
import { Countries } from '~/common/constants/countries.enum';
import { reportEvent } from '~/domains/analytics';
import { getReferralCode } from '~/domains/analytics/utils';
import { Routes } from '~/domains/common/constants/routes';
import { useCheckHandleAvailability } from '~/domains/channel/useCheckHandleAvailability';
import { BRANDS } from '~/domains/auth/consts';
import { useIsRestrictedDomain } from '~/domains/auth/hooks/useIsRestrictedDomains';
import { useIsFeatureEnabled } from '~/hooks/useIsFeatureEnabled';
import { FEATURE } from '~/common/enums/feature.enum';
import type { ProvePrefillVerifyResponse } from '~/services/identity/authenticated/prove-prefill.service.api';
import {
  createPrefilledKycSnapshot,
  didPrefilledValuesChange,
  type PrefilledKycSnapshot,
} from '~/domains/auth/utils/prefillKyc.utils';
import { useShowSplashNotification } from '~/domains/common/hooks/useShowSplashNotification';
import type { SocureKycStatus } from '~/services/users/types';
import type { AuthSuccessResponse } from '~/services/identity/unauthenticated/identity.service.api';
import { me } from '~/services/users/users.service.api';
import { patchCommissionerChannelData } from '~/services/commissioner/commissioner-service.service.api';
import { updatePersonalInfo } from '~/services/identity/authenticated/identity.service.api';
import { useClaimPromoCode } from '~/domains/promotions/domains/promoCodes/hooks/useClaimPromoCode';
import { PromotionActionLocation } from '~/domains/promotions/constants/analytics-enums';
import { logError } from '~/common/utils/log';
import useOpenIntercom from '~/components/Intercom/useOpenItercom';
import { mailToSupport } from '~/components/utils/mailToSupport';
import { RootModalsContext } from '~/domains/common/context/RootModalsContext/RootModalsContext';
import { AuthModalsContext } from '~/domains/auth/context/AuthModalsContext/AuthModalsContext';

import { useGetBranding } from '../../hooks/useGetBranding';
import useSignUp from '../../hooks/useSignUp';
import useKyc from '../../hooks/useKyc';
import { ProvePrefillStatus, useProvePrefill, VerifyType } from '../../hooks/useProvePrefill';
import { useUserSchema } from '../../hooks/useUserSchema';
import { setTokens } from '../../utils/setTokens';
import { removeTokens } from '../../utils/removeTokens';

import type { SignUpFormValues, KYCFormValues } from './types';
import useIsShouldSkipKycAfterSignUp from './useIsShouldSkipKycAfterSignUp';
import { getKycButtonLoading, NON_NAVIGATING_KYC_STATUSES } from './useSignUpStepper.utils';

interface UseSignUpStepperOptions {
  startFromKyc?: boolean;
}

function useSignUpStepper({ startFromKyc = false }: UseSignUpStepperOptions = {}) {
  const { brand } = useGetBranding();
  const { showSplashNotification } = useShowSplashNotification();
  const {
    user,
    setUser,
    getRedirect,
    clearRedirect,
    setRedirect,
    isSignUpInitialised,
    setIsSignUpInitialised,
  } = useContext(UserContext);
  const { signUpSchema, kycSchema } = useUserSchema();
  const router = useRouter();
  const { openIntercom, isIntercomAvailable } = useOpenIntercom();
  const { t } = useTranslation('auth');
  const {
    openModal: openRootModal,
    closeModalsByType: closeRootModalsByType,
    disableClose: disableRootClose,
    enableClose: enableRootClose,
  } = useContext(RootModalsContext);
  const { openModal: openAuthModal, closeModal: closeAuthModal } = useContext(AuthModalsContext);

  const prefilledSnapshotRef = useRef<PrefilledKycSnapshot | null>(null);

  const preAuthStepCount = 3;

  const { isShouldSkipKycAfterSignUpLoading, isShouldSkipKycAfterSignUp } =
    useIsShouldSkipKycAfterSignUp();

  function getDefaultStepIndex(): number {
    if (!user) return 0;
    if (startFromKyc) return preAuthStepCount;
    if (isSignUpInitialised) return preAuthStepCount - 1;
    if (isShouldSkipKycAfterSignUp) return preAuthStepCount - 1;
    return preAuthStepCount;
  }
  const defaultStepIndex = getDefaultStepIndex();
  const [activeStepIndex, setActiveStepIndex] = useState(defaultStepIndex);
  const [isCompletingAuth, setIsCompletingAuth] = useState(false);
  const [lastKycStatus, setLastKycStatus] = useState<SocureKycStatus | null>(null);

  const formSignUp = useForm<SignUpFormValues>({
    initialValues: {
      email: '',
      emailVerificationCode: '',
      password: '',
      handle: user?.handle ?? '',
      promoOrReferralCode: (router.query.referralCode as string) || getReferralCode() || '',
      firstName: '',
      lastName: '',

      // Hidden fields
      _promoModalProps: undefined,
      _promoCodeValue: undefined,
      _referralCodeValue: undefined,
    },
    validate: zodResolver(signUpSchema),
    validateInputOnChange: true,
  });

  const formKYC = useForm<KYCFormValues>({
    initialValues: {
      firstName: '',
      lastName: '',
      address: '',
      city: '',
      state: '',
      country: Countries.US,
      zip: '',
      phone: '',
      verificationCode: '',
      dob: {
        month: '',
        day: '',
        year: '',
      },
    },
    validate: zodResolver(kycSchema),
    validateInputOnChange: true,
    validateInputOnBlur: true,
  });

  // Pre-populate the (manual) KYC form's name from the persisted user profile
  // (signup values are lost when the flow navigates to /user-verification and
  // remounts). The empty-checks let a Prove prefill or a user edit win over this.
  useEffect(() => {
    if (!user?.id) return;
    if (!formKYC.values.firstName && user.firstName) {
      formKYC.setFieldValue('firstName', user.firstName);
    }
    if (!formKYC.values.lastName && user.lastName) {
      formKYC.setFieldValue('lastName', user.lastName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.firstName, user?.lastName]);

  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { _promoCodeValue, _referralCodeValue } = formSignUp.values;
  const { claimPromoCode } = useClaimPromoCode(_promoCodeValue, PromotionActionLocation.SIGN_UP);

  const completeAuthentication = useCallback(
    async (authData: AuthSuccessResponse) => {
      setIsSignUpInitialised(true);
      setTokens(authData);

      try {
        const userResponse = await me();
        const userData = userResponse?.data;

        if (!userData) {
          throw new Error('Failed to load user profile after sign-up');
        }

        window.sessionStorage.setItem('user', JSON.stringify(userData));
        setUser(userData);

        if (userData.handle) {
          formSignUp.setFieldValue('handle', userData.handle);
        }

        reportEvent('User > Sign up', {
          handle: userData.handle,
          email: userData.email,
          userId: userData.id,
          raf_code: _referralCodeValue,
          promo_code: _promoCodeValue,
          brand,
        });

        if (_promoCodeValue) {
          claimPromoCode();
        }

        return userData;
      } catch (error) {
        removeTokens();
        logError('Failed to complete authentication after sign-up', error);
        showSplashNotification({
          title: t('signUp.errors.authCompletionFailed.title'),
          message: t('signUp.errors.authCompletionFailed.message'),
          color: 'red',
          autoClose: 5000,
        });
        return null;
      }
    },
    [
      setIsSignUpInitialised,
      setUser,
      formSignUp,
      _referralCodeValue,
      _promoCodeValue,
      brand,
      claimPromoCode,
      showSplashNotification,
    ]
  );

  const resetToEmailStep = useCallback(() => {
    formSignUp.setFieldValue('password', '');
    setActiveStepIndex(0);
  }, [formSignUp]);

  const openTooManyAttemptsModal = useCallback(() => {
    openAuthModal({
      type: 'TooManyAttemptsModal',
      onContactSupport: () => {
        openIntercom("I'm having trouble verifying my email during sign-up.");
      },
      onCancel: () => {
        closeAuthModal();
        resetToEmailStep();
      },
    });
  }, [openAuthModal, closeAuthModal, openIntercom, resetToEmailStep]);

  const openEmailVerificationModal = useCallback(
    (verificationToken: string, email: string, canResendIn?: number) => {
      disableRootClose();
      openRootModal({
        type: 'EmailVerificationModal',
        verificationToken,
        email,
        canResendIn,
        onVerifySuccess: async (data) => {
          enableRootClose();
          setIsCompletingAuth(true);
          try {
            const userData = await completeAuthentication(data);
            if (userData) {
              setActiveStepIndex(preAuthStepCount - 1);
            }
          } finally {
            setIsCompletingAuth(false);
          }
        },
        onTooManyAttempts: () => {
          enableRootClose();
          openTooManyAttemptsModal();
        },
        onStartOver: () => {
          enableRootClose();
          resetToEmailStep();
        },
        onContactSupport: () => {
          if (isIntercomAvailable) {
            openIntercom("I'm having trouble verifying my email during sign-up.");
          } else {
            mailToSupport({
              subject: 'Email verification issue',
              body: "Hello, Splash Support - I'm having trouble verifying my email during sign-up.",
            });
          }
        },
      });
    },
    [
      openRootModal,
      disableRootClose,
      enableRootClose,
      completeAuthentication,
      preAuthStepCount,
      openTooManyAttemptsModal,
      resetToEmailStep,
      isIntercomAvailable,
      openIntercom,
    ]
  );

  const { handleSignUp, isSigningUp, errorCode, errorStatus, errorMessage } = useSignUp({
    formValues: formSignUp.values,
    onSignUpSuccess: async (result) => {
      if (router.query.redirectTo) {
        setRedirect(router.query.redirectTo as string);
      }

      if (result.mfaRequired) {
        openEmailVerificationModal(
          result.verificationToken,
          formSignUp.values.email,
          result.canResendIn
        );
      } else {
        const { response } = result as { mfaRequired: false; response: AuthSuccessResponse };
        setIsCompletingAuth(true);
        try {
          const userData = await completeAuthentication(response);
          if (userData) {
            setActiveStepIndex(preAuthStepCount - 1);
          }
        } finally {
          setIsCompletingAuth(false);
        }
      }
    },
  });

  const isFormLoading =
    isSigningUp ||
    isCompletingAuth ||
    (user && activeStepIndex >= preAuthStepCount && isShouldSkipKycAfterSignUpLoading);

  const {
    handleKyc,
    isLoading: isKycMutationLoading,
    isSuccess: isKycSuccess,
  } = useKyc({
    formValues: formKYC.values,
    onComplete: (status) => {
      setLastKycStatus(status);
      // Statuses that keep the user on the form (resubmit/missing) do not
      // navigate; everything else routes to the verification result screen.
      if (NON_NAVIGATING_KYC_STATUSES.has(status)) {
        return;
      }
      router.push(Routes.userVerification({ redirectTo: router.query.redirectTo as string }));
    },
  });
  const isKycLoading = getKycButtonLoading(isKycMutationLoading, isKycSuccess, lastKycStatus);

  const currentEmail = user ? user.email : formSignUp.values.email;
  const isRestrictedDomain = useIsRestrictedDomain({ email: currentEmail });

  useCheckHandleAvailability({
    form: formSignUp,
    enabled: true,
  });

  // ========= Navigation =========
  const [verifyType, setVerifyType] = useState<VerifyType | null>(null);
  const [isEditingFromConfirm, setIsEditingFromConfirm] = useState(false);

  const isProveKycEnabled = useIsFeatureEnabled(FEATURE.ENABLE_PROVE_KYC_WEB);

  // Build step count dynamically based on MFA requirement and KYC configuration.
  // Labels are sequential ("Step 1", "Step 2", ...) so we derive them from the count.
  const stepLabel = (index: number): string =>
    // eslint-disable-next-line local-rules/no-translate-cast
    t(`signUp.steps.step${index + 1}` as Parameters<typeof t>[0]);

  let stepCount = preAuthStepCount; // Email, Password, [EmailVerification], Handle

  if (brand === BRANDS.SPLASH && !isShouldSkipKycAfterSignUp) {
    if (!isProveKycEnabled) {
      stepCount += 1; // KYCDetails only
    } else {
      stepCount += 1; // KYCStart
      if (verifyType === VerifyType.Manual) {
        stepCount += 1; // KYCDetails
      } else {
        stepCount += 1; // ConfirmInfo (after Prove verification)
      }
    }
  }

  const steps: string[] = Array.from({ length: stepCount }, (_, i) => stepLabel(i));

  const prevStep = () => {
    if (activeStepIndex > 0) {
      setActiveStepIndex((current) => current - 1);
    }
  };

  const nextStep = () => {
    if (activeStepIndex < steps.length - 1) {
      setActiveStepIndex((current) => current + 1);
    }
  };

  // TODO: Move to constants after confirmation with Identity team
  const errorCodeToStepMap: Record<string, number> = {
    INVALID_PASSWORD: 2,
    SPLASH_EXPOSED_PASSWORD: 2,
    INVALID_RECAPTCHA_ERROR: 1,
    EMAIL_CONFLICT: 1,
    HANDLE_CONFLICT: preAuthStepCount,
    // Duplicate of HANDLE_CONFLICT, wrong value on the backend. Delete after
    // https://github.com/BetterPool/identity-service/pull/347 hits PROD
    Conflict: preAuthStepCount,
    // Added this because the response for adding `-splash` to the handle without a `@splashsports.com` email is generic, and to catch other potential generic errors
    'Bad Request': activeStepIndex + 1,
  };

  useEffect(() => {
    if (errorCode) {
      reportEvent('Onboarding > Sign up Error', { errorCode, errorStatus });

      if (errorCode === 'EMAIL_CONFLICT') {
        openAuthModal({
          type: 'ExistingAccountModal',
          email: formSignUp.values.email,
          onLogIn: () => {
            closeAuthModal();
            router.push(
              Routes.signIn({
                redirectTo: router.query.redirectTo as string,
                brand,
              })
            );
          },
          onUseDifferentEmail: () => {
            closeAuthModal();
            formSignUp.setFieldValue('email', '');
            formSignUp.setFieldValue('password', '');
            formSignUp.clearFieldError('email');
            setActiveStepIndex(0);
          },
        });
        return;
      }

      const mappedStep = errorCodeToStepMap[errorCode];
      const nextIndex = mappedStep ? mappedStep - 1 : 0;
      setActiveStepIndex(nextIndex);

      switch (errorCode) {
        case 'INVALID_PASSWORD':
        case 'SPLASH_EXPOSED_PASSWORD':
          formSignUp.setFieldError('password', errorMessage);
          break;
        case 'Conflict': // Deprecated (wrong) API error code support
        case 'HANDLE_CONFLICT':
          formSignUp.setFieldError('handle', errorMessage);
          break;
        default:
          break;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorCode]);

  // ========= Handlers =========
  const handleSetEmail = () => {
    reportEvent('Onboarding > Submit Email', {
      referralCode: formSignUp.values._referralCodeValue,
      promoCode: formSignUp.values._promoCodeValue,
    });
    nextStep();
  };

  const handleSetPassword = () => {
    reportEvent('Onboarding > Submit Password');
    handleSignUp();
  };

  const {
    mutate: submitHandle,
    isLoading: isHandleMutationLoading,
    isSuccess: isHandleSuccess,
  } = useMutation({
    mutationKey: ['setHandle'],
    mutationFn: async () => {
      const channelId = user?.channelId;
      if (!channelId) throw new Error('No channel ID');

      const { handle, firstName, lastName } = formSignUp.values;

      await patchCommissionerChannelData({ handle: handle.trim() }, channelId);

      await updatePersonalInfo({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      });

      // Refresh user context so the updated handle is reflected immediately
      const userResponse = await me();
      const updatedUser = userResponse?.data;
      if (updatedUser) {
        window.sessionStorage.setItem('user', JSON.stringify(updatedUser));
        setUser(updatedUser);
      }
    },
    onSuccess: () => {
      if (isShouldSkipKycAfterSignUp) {
        const redirectTo = getRedirect();
        if (redirectTo) {
          router.replace(redirectTo);
          clearRedirect();
        } else {
          router.replace((router.query.redirectTo as string) ?? Routes.home());
        }
        return;
      }

      router.replace(Routes.userVerification({ redirectTo: router.query.redirectTo as string }));
    },
    onError: (error: unknown) => {
      logError('Failed to set handle', error);
      const apiMessage = (error as { response?: { data?: { message?: string } } })?.response?.data
        ?.message;
      if (apiMessage) {
        formSignUp.setFieldError('handle', apiMessage);
      } else {
        showSplashNotification({
          title: t('signUp.errors.handleSetFailed.title'),
          message: t('signUp.errors.handleSetFailed.message'),
          color: 'red',
          autoClose: 5000,
        });
      }
    },
  });
  const isHandleLoading = isHandleMutationLoading || isHandleSuccess;

  const handleSetHandle = () => {
    const { handle, firstName, lastName } = formSignUp.values;
    const hasHandleError = 'handle' in formSignUp.errors || !handle?.trim();
    const hasNameError =
      'firstName' in formSignUp.errors ||
      'lastName' in formSignUp.errors ||
      !firstName?.trim() ||
      !lastName?.trim();
    // The handle step (first/last name + handle) is mandatory and cannot be
    // skipped, so never submit unless every required field is present and
    // valid. This also closes the Enter-key path, which previously validated
    // only the handle and could bypass the required name fields.
    if (hasHandleError || hasNameError) return;
    reportEvent('Onboarding > Submit Handle');
    if (!user?.channelId) return;
    submitHandle();
  };

  const handleSetAddress = () => {
    if (prefilledSnapshotRef.current) {
      const changed = didPrefilledValuesChange(prefilledSnapshotRef.current, formKYC.values);
      reportEvent('KYC > Verify Prefilled Info', { madeChanges: Boolean(changed) });
    }
    handleKyc();
  };

  // ======= Prove Prefill integration =======
  const {
    startProvePrefill,
    submitOtpCode,
    status: proveStatus,
  } = useProvePrefill({
    onRequestOtpCode: async () => formKYC.values.verificationCode?.trim() || null,
    onError: (error) => {
      reportEvent('KYC > Prefill Failed', { error });
      enableRootClose();
      closeRootModalsByType('ProveVerificationModal');
      formKYC.setFieldValue('verificationCode', '');
      setVerifyType(VerifyType.Manual);
      setActiveStepIndex(preAuthStepCount + 1); // go to KYCDetails (manual fallback)
      showSplashNotification({
        title: t('signUp.errors.prove.errorVerifyingIdentityTitle'),
        message: t('signUp.errors.prove.errorVerifyingIdentityMessage'),
        color: 'yellow',
        autoClose: 5000,
      });
    },
    onVerify: (data: ProvePrefillVerifyResponse) => {
      // Prefill name
      if (data?.firstName) formKYC.setFieldValue('firstName', String(data.firstName));
      if (data?.lastName) formKYC.setFieldValue('lastName', String(data.lastName));

      // Prefill phone
      if (data?.phoneNumber) formKYC.setFieldValue('phone', String(data.phoneNumber));

      // Prefill DOB
      if (data?.dateOfBirth) {
        const { day, month, year } = data.dateOfBirth;
        if (day) formKYC.setFieldValue('dob.day', String(day));
        if (month) formKYC.setFieldValue('dob.month', String(month));
        if (year) formKYC.setFieldValue('dob.year', String(year));
      }

      // Prefill address from first entry
      const addr = data?.addresses?.[0];
      if (addr) {
        if (addr.address) formKYC.setFieldValue('address', addr.address);
        if (addr.city) formKYC.setFieldValue('city', addr.city);
        if (addr.region) formKYC.setFieldValue('state', addr.region);
        if (addr.postalCode) formKYC.setFieldValue('zip', addr.postalCode);
      }

      // Close verification modal and navigate to ConfirmInfo step
      enableRootClose();
      closeRootModalsByType('ProveVerificationModal');
      setActiveStepIndex(preAuthStepCount + 1); // ConfirmInfo step

      // Save the prefilled snapshot for verification check for Analytics
      prefilledSnapshotRef.current = createPrefilledKycSnapshot(data);
    },
  });

  const isProveInitializing =
    proveStatus === ProvePrefillStatus.Initializing ||
    proveStatus === ProvePrefillStatus.Authenticating;

  // Open the verification modal when Prove is ready for OTP or instant link
  useEffect(() => {
    if (proveStatus === ProvePrefillStatus.AwaitingOtp) {
      openRootModal({
        type: 'ProveVerificationModal',
        phone: formKYC.values.phone,
        isAwaitingInstantLink: false,
        onSubmitCode: (code: string) => {
          reportEvent('KYC > Verify Code');
          submitOtpCode(code);
        },
        onTryAgain: () => {
          reportEvent('KYC > Resend Code');
          enableRootClose();
          closeRootModalsByType('ProveVerificationModal');
        },
      });
    } else if (proveStatus === ProvePrefillStatus.AwaitingInstantLink) {
      disableRootClose();
      openRootModal({
        type: 'ProveVerificationModal',
        phone: formKYC.values.phone,
        isAwaitingInstantLink: true,
        onSubmitCode: (code: string) => {
          reportEvent('KYC > Verify Code');
          submitOtpCode(code);
        },
        onTryAgain: () => {
          reportEvent('KYC > Resend Code');
          enableRootClose();
          closeRootModalsByType('ProveVerificationModal');
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proveStatus]);

  const handleSelectVerifyType = (type: VerifyType) => {
    reportEvent(`Onboarding > ${type === VerifyType.Prove ? 'Prefill KYC' : 'Manual KYC'}`);
    setVerifyType(type);
    if (type === VerifyType.Manual) {
      setActiveStepIndex(preAuthStepCount + 1); // KYCDetails step
    }
  };

  const handleProveSendCode = () => {
    reportEvent('KYC > Send Verification Code');
    startProvePrefill({
      flowType: undefined, // auto-detect mobile/desktop
      phoneNumber: formKYC.values.phone,
      dateOfBirth: {
        day: Number(formKYC.values.dob.day),
        month: Number(formKYC.values.dob.month),
        year: Number(formKYC.values.dob.year),
      },
    });
  };

  const handleEditInfo = () => {
    reportEvent('KYC > Edit Prefilled Info');
    setIsEditingFromConfirm(true);
    setVerifyType(VerifyType.Manual);
    setActiveStepIndex(preAuthStepCount + 1); // KYCDetails step (with mode='edit')
  };

  const handleSkipKYC = () => {
    reportEvent('KYC > Skip', { step: activeStepIndex + 1 });

    const redirectTo = getRedirect();

    if (redirectTo) {
      router.replace(redirectTo);
      clearRedirect();
    } else {
      router.replace((router.query.redirectTo as string) || Routes.home());
    }
  };

  const handleKeyDownHandle = (event: KeyboardEvent) => {
    // Handle step is at preAuthStepCount - 1
    if (activeStepIndex === preAuthStepCount - 1 && event.key === 'Enter') {
      event.preventDefault();
      handleSetHandle();
    }
  };

  return {
    activeStepIndex,
    brand,
    formKYC,
    formSignUp,
    isFormLoading,
    prevStep,
    steps,
    user,
    isRestrictedDomain,
    isProveKycEnabled,
    isProveInitializing,
    handleSetEmail,
    handleSetPassword,
    handleSetHandle,
    isHandleLoading,
    handleSetAddress,
    handleSkipKYC,
    handleKeyDownHandle,
    verifyType,
    handleSelectVerifyType,
    handleProveSendCode,
    handleEditInfo,
    isEditingFromConfirm,
    isKycLoading,
    preAuthStepCount,
  };
}

export default useSignUpStepper;
